import { formatDate } from '@seen/core/dates'
import * as appointments from '@seen/core/models/appointments/index'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ParticipationModeSubType } from '@seen/core/models/communications/constants'
import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { OrderLabel } from '@seen/core/models/orderLabel'
import { Order } from '@seen/core/models/orders/Order'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { getReferenceString } from '@seen/core/references'
import { DotSeparator } from '@seen/ui'
import { groupBy, isEqual, orderBy, sortBy } from 'lodash-es'
import { type JSX, type ReactNode, useState } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import {
  LabelChangeAction,
  type LabelClass,
} from '#src/components/activity/LabelChangeAction.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { DocumentReferenceSheet } from '#src/components/document-reference/DocumentReferenceSheet.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { useOrderDocumentReferences } from '#src/components/order/useOrderDocumentReferences.ts'
import { useEncounterActivity } from '#src/hooks/useEncounterActivity.tsx'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import {
  useBulkResourceVersions,
  useResourceVersions,
} from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { AppointmentLink } from '#src/pages/participants/timeline/AppointmentLink.tsx'
import { FaxItem } from '#src/pages/participants/timeline/FaxItem.tsx'
import { useTaskActivity } from '#src/pages/task/UseTaskActivity.tsx'
import {
  groupOrderLabelActivityEntries,
  groupTaskLabelActivityEntries,
  type LabelChangeData,
} from '#src/utils/GroupLabelActivityEntries.tsx'

export function OrderActivity({ order }: { order: Order }): JSX.Element {
  const { data: pastOrders = [] } = useResourceVersions(Order, order.id)
  const sortedOrders = sortBy(pastOrders, (t: Order) => t.lastUpdated)
  const orderOriginOf = useVersionOriginTask(sortedOrders)

  const orderActivity = sortedOrders.reduce<
    {
      action: ReactNode
      order: Order
      key?: string
      labelChange?: LabelChangeData
    }[]
  >((acc, curr, idx) => {
    if (acc.length === 0) {
      return [
        {
          action: `order created${curr.elationId ? ' from Elation' : ''}`,
          order: curr,
          key: `${curr.versionId}-created`,
        },
      ]
    }

    const prev = sortedOrders[idx - 1]

    if (curr.status !== prev?.status) {
      if (curr.status === 'on-hold' && prev?.status !== 'on-hold') {
        acc.push({
          action: 'order submitted',
          order: curr,
          key: `${curr.versionId}-submitted`,
        })
        return acc
      }

      if (curr.signedBy && !prev?.signedBy) {
        acc.push({
          action: 'order signed',
          order: curr,
          key: `${curr.versionId}-signed`,
        })
        return acc
      }

      if (curr.status === 'active' && prev?.status !== 'active') {
        acc.push({
          action: 'order in progress',
          order: curr,
          key: `${curr.versionId}-signed`,
        })
        return acc
      }

      if (curr.status === 'coordinated' && prev?.status !== 'coordinated') {
        acc.push({
          action: 'order coordinated',
          order: curr,
          key: `${curr.versionId}-coordinated`,
        })
        return acc
      }

      if (curr.status === 'effectuated' && prev?.status !== 'effectuated') {
        acc.push({
          action: 'order effectuated',
          order: curr,
          key: `${curr.versionId}-effectuated`,
        })
        return acc
      }

      if (curr.status === 'completed' && prev?.status !== 'completed') {
        acc.push({
          action: 'order completed',
          order: curr,
          key: `${curr.versionId}-completed`,
        })
        return acc
      }

      if (curr.status === 'revoked' && prev?.status !== 'revoked') {
        acc.push({
          action: 'order revoked',
          order: curr,
          key: `${curr.versionId}-revoked`,
        })
        return acc
      }
    }

    if (curr.authorizationId !== prev?.authorizationId) {
      if (curr.authorizationId && !prev?.authorizationId) {
        acc.push({
          action: `authorization linked: ${curr.authorizationId}`,
          order: curr,
          key: `${curr.versionId}-auth-added`,
        })
        return acc
      }
      if (!curr.authorizationId && prev?.authorizationId) {
        acc.push({
          action: `authorization unlinked: ${prev.authorizationId}`,
          order: curr,
          key: `${curr.versionId}-auth-removed`,
        })
        return acc
      }
      if (curr.authorizationId && prev?.authorizationId) {
        acc.push({
          action: `authorization changed: ${prev.authorizationId} -> ${curr.authorizationId}`,
          order: curr,
          key: `${curr.versionId}-auth-changed`,
        })
        return acc
      }
    }

    const prevLabels = prev?.labels ?? []
    const currLabels = curr.labels ?? []

    if (!isEqual(sortBy(prevLabels), sortBy(currLabels))) {
      const added = currLabels.filter((l) => !prevLabels.includes(l))
      const removed = prevLabels.filter((l) => !currLabels.includes(l))

      if (added.length > 0 || removed.length > 0) {
        acc.push({
          action: (
            <LabelChangeAction
              added={added}
              removed={removed}
              LabelClass={OrderLabel}
            />
          ),
          order: curr,
          key: `${curr.versionId}-labels`,
          labelChange: {
            added,
            removed,
            LabelClass: OrderLabel as LabelClass,
          },
        })
        return acc
      }
    }

    return acc
  }, [])

  const relatedAppointmentsQuery = useSearch(appointments.ExtendedAppointment, {
    ...appointments.ExtendedAppointment.byBasedOn(order.reference),
    status: ['booked', 'cancelled'],
  })
  const relatedAppointments = relatedAppointmentsQuery.data ?? []
  const appointmentActivity = useAppointmentsActivity(relatedAppointments)
  const encounterActivity = useEncounterActivity(relatedAppointments, {
    includeAppointmentLink: true,
  })

  const [selectedDocument, setSelectedDocument] =
    useState<ExtendedDocumentReference | null>(null)
  const { explicit: documentReferences } = useOrderDocumentReferences(order)
  const documentReferencesActivity = useDocumentReferencesActivity(
    documentReferences ?? [],
    setSelectedDocument
  )

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([order.reference])
  )

  const { data: relatedTasks = [] } = useSearch(
    StandardTask,
    StandardTask.byFocus(order.reference)
  )

  const { data: relatedFaxes = [] } = useSearch(ExtendedCommunication, {
    _sort: '-_lastUpdated',
    _count: '1000',
    'status:not': 'entered-in-error',
    medium: ParticipationModeSubType.FAX,
    'based-on': order.reference,
  })

  const medplum = useMedplum()
  const validRelatedTasks = relatedTasks.filter(Boolean)
  const { data: relatedTaskVersions = [] } = useBulkResourceVersions(
    StandardTask,
    validRelatedTasks.map((task) => task.id),
    {
      validate: (version) => {
        // Historical signature-task states were not migrated to the
        // standard-task schema, so force state parsing before rendering.
        void version.state
      },
    }
  )
  const relatedTasksHistories = Object.values(
    groupBy(relatedTaskVersions, 'id')
  )
  const relatedTaskActivity = useTaskActivity(relatedTasksHistories)

  const relatedTasksCommentsQuery = useQuery({
    queryKey: ['relatedTasksComments', validRelatedTasks.map((t) => t.id)],
    queryFn: () =>
      Promise.all(
        validRelatedTasks.map((task) =>
          medplum.searchResourceClass(
            CommentCommunication,
            CommentCommunication.searchQuery([task.reference])
          )
        )
      ).then((results) => results.flat()),
  })
  const relatedTasksComments = relatedTasksCommentsQuery.data ?? []

  const relatedAppointmentsCommentsQuery = useQuery({
    queryKey: [
      'relatedAppointmentsComments',
      relatedAppointments.map((a) => a.id) ?? [],
    ],
    queryFn: () =>
      Promise.all(
        relatedAppointments.map((a) =>
          medplum.searchResourceClass(
            CommentCommunication,
            CommentCommunication.searchQuery([a.reference])
          )
        )
      ).then((results) => results.flat()),
  })
  const relatedAppointmentsComments =
    relatedAppointmentsCommentsQuery.data ?? []

  const faxActivity = relatedFaxes.map((fax) => ({
    fax,
    key: `${fax.id}-fax`,
  }))

  // Apply label grouping to order and task activity before combining
  const groupedOrderActivity = groupOrderLabelActivityEntries(orderActivity)
  const groupedTaskActivity = groupTaskLabelActivityEntries(relatedTaskActivity)

  const all = orderBy(
    [
      ...groupedOrderActivity,
      ...groupedTaskActivity,
      ...comments,
      ...relatedTasksComments,
      ...appointmentActivity,
      ...encounterActivity,
      ...documentReferencesActivity,
      ...relatedAppointmentsComments,
      ...faxActivity,
    ],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      if ('fax' in a) {
        return a.fax.sent
      }
      if ('appointment' in a) {
        return a.appointment.lastUpdated
      }
      if ('documentReference' in a) {
        return a.documentReference.lastUpdated
      }
      if ('task' in a) {
        return a.task.lastUpdated
      }
      if ('encounter' in a) {
        return a.encounter.lastUpdated
      }
      return a.order.lastUpdated
    },
    ['desc']
  )

  return (
    <>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment
              key={entry.reference.reference}
              comment={entry}
              showRelated={
                !entry.partOf
                  ?.map(getReferenceString)
                  .some((r) => r === order.reference.reference)
              }
            />
          )
        }
        if ('fax' in entry) {
          return <FaxItem key={entry.key} fax={entry.fax} />
        }
        if ('appointment' in entry) {
          return (
            <ActivityEntry
              key={entry.key}
              author={entry.appointment.lastUpdatedBy}
              time={entry.appointment.lastUpdated}
              action={entry.action}
            />
          )
        }
        if ('documentReference' in entry) {
          return (
            <ActivityEntry
              key={entry.key}
              author={entry.documentReference.lastUpdatedBy}
              time={entry.documentReference.lastUpdated}
              action={entry.action}
            />
          )
        }
        if ('task' in entry) {
          return (
            <ActivityEntry
              key={entry.key ?? entry.task.versionId}
              author={entry.task.lastUpdatedBy}
              time={entry.task.lastUpdated}
              action={
                <>
                  <HoverTask task={entry.task} className="mr-1" />
                  <span className="text-muted-foreground">·</span>{' '}
                  {entry.action}
                </>
              }
            />
          )
        }
        if ('encounter' in entry) {
          return (
            <ActivityEntry
              key={entry.key ?? entry.encounter.versionId}
              author={entry.encounter.lastUpdatedBy}
              time={entry.encounter.lastUpdated}
              action={entry.action}
            />
          )
        }
        return (
          <ActivityEntry
            key={entry.key ?? entry.order.versionId}
            author={entry.order.lastUpdatedBy}
            time={entry.order.lastUpdated}
            action={entry.action}
            originTask={orderOriginOf(entry.order)}
          />
        )
      })}
      <DocumentReferenceSheet
        documentReference={selectedDocument}
        open={!!selectedDocument}
        setOpen={(open) => {
          if (!open) {
            setSelectedDocument(null)
          }
        }}
      />
    </>
  )
}

const useAppointmentsActivity = (
  appts: appointments.ExtendedAppointment[]
): {
  action: ReactNode
  appointment: appointments.ExtendedAppointment
  key?: string
}[] => {
  const appointmentsIds = appts.map((a) => a.id)
  const { data: appointmentHistory = [] } = useBulkResourceVersions(
    appointments.ExtendedAppointment,
    appointmentsIds
  )

  const grouped = groupBy(appointmentHistory, 'id')
  const activity = Object.entries(grouped).flatMap(([_, history]) =>
    history.reduce<
      {
        action: ReactNode
        appointment: appointments.ExtendedAppointment
        key?: string
      }[]
    >((acc, curr) => {
      const prev = acc[acc.length - 1]

      if (curr.status !== prev?.appointment.status) {
        if (curr.status === 'cancelled') {
          acc.push({
            action: (
              <>
                cancelled
                <AppointmentLink
                  className="ml-1 text-xs underline"
                  appointment={curr}
                >
                  {curr.title}
                  {curr.start ? ` for ${formatDate(curr.start)}` : ''}
                </AppointmentLink>
                {curr.comment && (
                  <>
                    <DotSeparator />
                    <span className="text-xs text-muted-foreground italic">
                      "{curr.comment}"
                    </span>
                  </>
                )}
              </>
            ),
            appointment: curr,
            key: `${curr.versionId}-cancelled`,
          })
          return acc
        }
        if (curr.status === 'booked') {
          acc.push({
            action: (
              <>
                scheduled
                <AppointmentLink
                  className="ml-1 text-xs underline"
                  appointment={curr}
                >
                  {curr.title}
                  {curr.start ? ` for ${formatDate(curr.start)}` : ''}
                </AppointmentLink>
              </>
            ),
            appointment: curr,
            key: `${curr.versionId}-booked`,
          })
          return acc
        }
      }

      return acc
    }, [])
  )

  return orderBy(activity, (a) => a.appointment.lastUpdated, ['desc'])
}

const useDocumentReferencesActivity = (
  docRefs: ExtendedDocumentReference[],
  onTitleClick: (doc: ExtendedDocumentReference) => void
): {
  action: ReactNode
  documentReference: ExtendedDocumentReference
  key?: string
}[] => {
  const documentReferencesIds = docRefs.map((d) => d.id)
  const { data: documentReferenceHistory = [] } = useBulkResourceVersions(
    ExtendedDocumentReference,
    documentReferencesIds
  )

  const grouped = groupBy(documentReferenceHistory, 'id')
  const activity = Object.entries(grouped).flatMap(([_, history]) => {
    const firstEntry = sortBy(history, 'lastUpdated')[0]
    if (!firstEntry) {
      return []
    }

    return [
      {
        action: (
          <>
            linked document{' '}
            <button
              type="button"
              onClick={() => onTitleClick(firstEntry)}
              className="cursor-pointer text-left font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden"
            >
              {firstEntry.displayTitle}
            </button>
          </>
        ),
        documentReference: firstEntry,
        key: `${firstEntry.versionId}-created`,
      },
    ]
  })
  return orderBy(activity, (a) => a.documentReference.lastUpdated, ['desc'])
}
